import type { QueryClient, UseQueryResult } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { EventIdPublic } from "@shared/schemas/branded-id.schemas";
import {
  EventParticipantRosterSchema,
  type EventParticipantRoster,
} from "@shared/schemas/event-participant-roster.schemas";
import { BaseApiHooksRepository } from "../../base-api-hooks.repository";
import { resolvePhotoUrls } from "@shared/utils/participant-photo-url.utils";
import { createParticipantRosterApi } from "./participant-roster.api";

/**
 * Its own key on purpose: same URL as the admin roster but at the generic tier,
 * and one cache key holding two shapes is a first-writer-wins bug.
 */
export const PARTICIPANT_ROSTER_QUERY_KEYS = {
  byEvent: (eventIdPublic: EventIdPublic) =>
    ["participant-roster", "event", eventIdPublic] as const,
} as const;

// Mirrors the admin roster: it changes on import/removal, never on the polling
// any one consumer does alongside it.
const ROSTER_STALE_TIME_MS = 5 * 60_000;

interface GetParticipantRosterOptions {
  readonly enabled?: boolean;
}

export class ParticipantRosterHooksHttpRepositoryImpl extends BaseApiHooksRepository {
  private readonly api;

  constructor(queryClient: QueryClient, apiClient: AxiosInstance) {
    super(queryClient);
    this.api = createParticipantRosterApi(apiClient);
  }

  createHooks() {
    const { api } = this;
    return {
      useGetParticipantRoster: (
        eventIdPublic: EventIdPublic,
        options: GetParticipantRosterOptions = {},
      ): UseQueryResult<EventParticipantRoster> => {
        const { enabled = true } = options;

        return this.query({
          key: PARTICIPANT_ROSTER_QUERY_KEYS.byEvent(eventIdPublic),
          fn: () => api.getParticipantRoster(eventIdPublic),
          parse: (dto) => ({
            participants: resolvePhotoUrls(
              EventParticipantRosterSchema.parse(dto).participants,
              EventIdPublic.unwrap(eventIdPublic),
            ),
          }),
          enabled: enabled && EventIdPublic.unwrap(eventIdPublic).length > 0,
          staleTime: ROSTER_STALE_TIME_MS,
        });
      },
    };
  }
}
